import { ViewedAssetsResponse } from '@modules/alerts/api/alerts'
import { POneEvent } from '@modules/auth/hooks/types'
import { EventParticipantProps } from '@modules/profile/types'
import { mapApiProfileTypeToUI } from '@shared/utils'

import {
  AnyResultProfile,
  CompanyAssetHistoryProfile,
  CompanyHistoryProfile,
  Delegate,
  DelegateHistoryProfile,
  EntityProfile,
  ProfileType,
} from '../types'
import { getApiClient } from './apiClient'

export const fetchCompanyProfileFn = async (params: { id: string }): Promise<CompanyHistoryProfile> => {
  const response = await getApiClient().get(`/company/${params.id}`)

  return response.data
}

export const fetchDelegateProfileFn = async (params: { id: string }): Promise<DelegateHistoryProfile> => {
  const response = await getApiClient().get(`/delegate/${params.id}`)

  return response.data
}

export const fetchAssetProfileFn = async (params: { id: string }): Promise<CompanyAssetHistoryProfile> => {
  const response = await getApiClient().get(`/company-asset/${params.id}`)

  return response.data
}

export const fetchLatestAssetProfilesFn = async (params: {
  companyId: number
  from?: number
  size: number
  types: { type: ProfileType }[]
  pageParam?: number
}): Promise<{ result: CompanyAssetHistoryProfile[]; facets: Record<ProfileType, number>; total: number }> => {
  const { from, size, types, companyId, pageParam } = params

  const body = {
    from: pageParam ? pageParam * size : from || 0,
    size,
    types,
  }

  const response = await getApiClient().post(`/company-asset/latest/${companyId}`, JSON.stringify(body))

  return {
    ...response.data,
    result: mapApiProfileTypeToUI(response.data?.result),
  }
}

export interface FetchEventGroupedAssetProfilesFnParams {
  companyId: number
  from?: number
  size: number
  types: { type: ProfileType }[]
  codes?: number[]
}

export interface FetchEventGroupedAssetProfilesFnResponse {
  result: { items: CompanyAssetHistoryProfile[]; event: EntityProfile['eventData'] }[]
  facets: Record<ProfileType, number>
  newAssetCounts: { facets: Record<ProfileType, number>; perPage: Record<string, number> }
  total: number
}

export interface MarkAssetsAsViewedFnParams {
  companyId: number
  assetIds: (number | string)[]
}

export const fetchEventGroupedAssetProfilesFn = async (
  params: FetchEventGroupedAssetProfilesFnParams & ViewedAssetsResponse,
): Promise<FetchEventGroupedAssetProfilesFnResponse> => {
  const { from, size, types, companyId, codes, viewedAssets, alertCreatedAt } = params

  const body = {
    from: from || 0,
    size,
    types,
    codes,
    viewedAssetIds: viewedAssets,
    countSinceDate: alertCreatedAt,
  }

  const response = await getApiClient().post(`/company-asset/all/${companyId}`, JSON.stringify(body))

  return {
    ...response.data,
    result: response.data?.result?.map((i: { items: CompanyAssetHistoryProfile[]; event: EntityProfile['eventData'] }) => ({
      ...i,
      items: mapApiProfileTypeToUI(i?.items ?? []),
    })),
  }
}

export const markAssetsAsViewed = async (params: MarkAssetsAsViewedFnParams): Promise<void> => {
  const body = {
    assetIds: params.assetIds,
  }

  await getApiClient().post(`/company-asset/mark-as-viewed/${params.companyId}`, JSON.stringify(body))
}

export const fetchAllAssetFilters = async (params: {
  companyId: number
  viewedAssetsData?: ViewedAssetsResponse
}): Promise<{ events: POneEvent[] }> => {
  const { viewedAssetsData } = params

  const response = await getApiClient().post(
    `/company-asset/filters/${params.companyId}`,
    JSON.stringify({ viewedAssetIds: viewedAssetsData?.viewedAssets, countSinceDate: viewedAssetsData?.alertCreatedAt }),
  )

  return response.data
}

export interface FetchAttendeesFnResponse {
  result: EventParticipantProps[]
  event: POneEvent
  total: number
}
export const fetchAttendees = async (params: {
  companyId: number
  size: number
  pageParam?: number
}): Promise<FetchAttendeesFnResponse> => {
  const { size, companyId, pageParam } = params
  const body = {
    from: pageParam ? pageParam * size : 0,
    size,
    companyId,
  }
  const { data } = await getApiClient().post(`/company/${params.companyId}/attendees`, JSON.stringify(body))

  return data
}

export interface FetchCompanyEventsFnResponse {
  events: POneEvent[]
}
export const fetchCompanyEvents = async (params: { companyId?: number }): Promise<FetchCompanyEventsFnResponse> => {
  const { data } = await getApiClient().get(`/company/${params.companyId}/events`)

  return data
}

export interface FetchCompanyDelegatesFnResponse {
  delegates: Delegate[]
  total: number
}
export const fetchCompanyDelegates = async (params: {
  companyId?: number
  eventId?: number
  size?: string
}): Promise<FetchCompanyDelegatesFnResponse> => {
  const { companyId, eventId, size } = params

  const getQueryParams = () => {
    if (eventId) {
      return `?companyId=${companyId}&eventId=${eventId}`
    }
    if (size) {
      return `?companyId=${companyId}&size=${size}`
    }
    return `?companyId=${companyId}`
  }

  const { data } = await getApiClient().get(`/delegate${getQueryParams()}`)

  return data
}

export const fetchNewAssetsCount = async (params: { companyId?: number; lastViewedAt?: string }): Promise<{ count: number }> => {
  const { companyId, lastViewedAt } = params

  const { data } = await getApiClient().get(`/company/${companyId}/new-assets-count?dateFrom=${lastViewedAt}`)

  return data
}

export interface FetchShortlistPageDataResponse {
  profiles: AnyResultProfile[]
}

export type FetchShortlistPageDataParams = {
  profiles: {
    id: string | number
    type: ProfileType
  }[]
}
